import {
  Box,
  Button,
  IconButton,
  Skeleton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationRequest, LocationResponse } from '../../app/services/api/generated';
import { Edit05, Plus, Trash01 } from '../../assets';
import { TableHeader } from '../../components/forms/TableHeader';
import PageArea from '../../components/shared/PageArea';
import { Pill } from '../../components/shared/Pill';
import { ConfirmationDeleteDialog } from '../../components/shared/dialogs/ConfirmationDeleteDialog';
import { PageTitleWithButtons } from '../../components/shared/layout/PageTitleWithButtons';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import useNswagClient from '../../hooks/api/useNswagClient';
import useLogError from '../../hooks/useLogError';
import { extractErrorMessage } from '../../utils';
import MessagesSnackbar from './components/MessagesSnackbar';
import StockCountZonesCreateEditDialog from './components/StockCountZonesCreateEditDialog';
import { EmptyTable } from '../../components/shared/EmptyTable';

const StockCountZonesPage = () => {

  const { t } = useTranslation('stockCountZones');
  const { getStoreLocations, createLocation, deleteLocation, updateLocation } = useNswagClient();
  const { logError } = useLogError();
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const [hasPermissionToReadZones, setHasPermissionToReadZones] = useState<boolean>(false);
  const [hasPermissionToWriteZones, setHasPermissionToWriteZones] = useState<boolean>(false);
  const [data, setData] = useState<LocationResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMoreThanNineZones, setHasMoreThanNineZones] = useState<boolean>(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
  const [isSubmitZoneProcessing, setIsSubmitZoneProcessing] = useState<boolean>(false);
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState<boolean>(false);
  const [isErrorNameTxtF, setIsErrorNameTxtF] = useState<boolean>(false);
  const [errorNameTxtFDescr, setErrorNameTxtFDescr] = useState<string>('');
  const [nameTxtFValue, setNameTxtFValue] = useState<string>('');
  const [zoneCreationErrorResponse, setZoneCreationErrorResponse] = useState<string>('');
  const [showCreationSuccessAlert, setShowCreationSuccessAlert] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isDeleteZoneProcessing, setIsDeleteZoneProcessing] = useState<boolean>(false);
  const [zoneDeletionErrorResponse, setZoneDeletionErrorResponse] = useState<string>('');
  const [showDeletionSuccessAlert, setShowDeletionSuccessAlert] = useState<boolean>(false);
  const [candidateZoneToDelete, setCandidateZoneToDelete] = useState<number | undefined>();
  const [candidateZoneToEdit, setCandidateZoneToEdit] = useState<number | undefined>();

  useEffect(() => {
    if (selectedStore?.storeNumber) {
      setHasPermissionToReadZones(hasPermissionTo(['StockLocationRead']));
      setHasPermissionToWriteZones(hasPermissionTo(['StockLocationWrite']));
      if (hasPermissionTo(['StockLocationRead'])) {
        loadZones();
      }
    }
  }, [selectedStore]);

  const loadZones = async () => {
    setIsLoading(true);
    await fetchZones(selectedStore?.storeNumber ?? '');
    setIsLoading(false);
  };

  const fetchZones = async (storeNumber: string) => {
    try {
      const zonesResponse = await getStoreLocations(storeNumber);
      if (zonesResponse?.data) {
        setData(zonesResponse.data);
        setHasMoreThanNineZones(zonesResponse.data.length > 9);
      }
    } catch (error) {
      logError(`Error loading data:', ${error}`);
    }
  };

  const handleNewZoneBtnClick = async () => {
    setIsSubmitBtnDisabled(true);
    setIsErrorNameTxtF(false);
    setErrorNameTxtFDescr('');
    setNameTxtFValue('');
    setZoneCreationErrorResponse('');
    setIsSubmitZoneProcessing(false);
    setIsCreateDialogOpen(true);
  };

  const handleDeleteZoneBtnClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    setCandidateZoneToDelete(id);
    setZoneDeletionErrorResponse('');
    setIsDeleteZoneProcessing(false);
    setIsDeleteDialogOpen(true);
  };

  const handleEditZoneBtnClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    setCandidateZoneToEdit(id);
    setIsSubmitBtnDisabled(true);
    setIsErrorNameTxtF(false);
    setErrorNameTxtFDescr('');
    setNameTxtFValue(data.find(location => location.id === id)?.name ?? '');
    setZoneCreationErrorResponse('');
    setIsSubmitZoneProcessing(false);
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = async () => {
    setCandidateZoneToEdit(undefined);
    setIsCreateDialogOpen(false);
    setZoneCreationErrorResponse('');
  };

  const handleCloseDeleteDialog = async () => {
    setIsDeleteDialogOpen(false);
    setZoneDeletionErrorResponse('');
  };

  const handleNameTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setNameTxtFValue(newValue);

    if (data.some(location => location.name === newValue)) {
      setIsErrorNameTxtF(true);
      setErrorNameTxtFDescr(t('createDialog.txtFNameError'));
      setIsSubmitBtnDisabled(true);
    }
    else {
      setIsErrorNameTxtF(false);
      setErrorNameTxtFDescr('');
      setIsSubmitBtnDisabled(false);
    }

    if (newValue === '')
      setIsSubmitBtnDisabled(true);
  };

  const handleAddNewZoneConfirm = async () => {
    setIsSubmitZoneProcessing(true);
    if (nameTxtFValue !== '' && selectedStore?.storeNumber) {
      const body: LocationRequest = {
        name: nameTxtFValue.trim(),
        storeNumber: selectedStore.storeNumber,
      };
      createLocation(body).then((result) => {
        if (result.data?.isSuccess) {
          setIsSubmitZoneProcessing(false);
          setIsCreateDialogOpen(false);
          setShowCreationSuccessAlert(true);
          loadZones();
        }
      }).catch((error) => {
        logError(error);
        const errorMessage = extractErrorMessage(error);
        setZoneCreationErrorResponse(errorMessage ?? t('createDialog.errorResponseConfiguration', { storeName: selectedStore?.storeName }));
        setIsSubmitZoneProcessing(false);
      });
    }
  };

  const handleEditZoneConfirm = async () => {
    setIsSubmitZoneProcessing(true);

    if (nameTxtFValue !== '' && candidateZoneToEdit && selectedStore?.storeNumber) {
      updateLocation({
        id: candidateZoneToEdit,
        name: nameTxtFValue.trim(),
        storeNumber: selectedStore.storeNumber,
      }).then((result) => {
        if (result?.success) {
          setIsSubmitZoneProcessing(false);
          setIsCreateDialogOpen(false);
          setShowCreationSuccessAlert(true);
          setCandidateZoneToEdit(undefined);
          loadZones();
        }
      }).catch((error) => {
        logError(error);
        const errorMessage = extractErrorMessage(error);
        setZoneCreationErrorResponse(errorMessage ?? t('editDialog.errorResponseConfiguration', { storeName: selectedStore?.storeName }));
        setIsSubmitZoneProcessing(false);
      });
    }
  };

  const handleDeleteZoneConfirm = async () => {
    setIsDeleteZoneProcessing(true);

    if (candidateZoneToDelete) {
      deleteLocation(candidateZoneToDelete).then((result) => {
        if (result.success) {
          setIsDeleteZoneProcessing(false);
          setIsDeleteDialogOpen(false);
          setShowDeletionSuccessAlert(true);
          loadZones();
        }
      }).catch((error) => {
        logError(error);
        const errorMessage = extractErrorMessage(error);
        setZoneDeletionErrorResponse(errorMessage ?? t('deleteDialog.errorResponse'));
        setIsDeleteZoneProcessing(false);
      });
    }
  };

  return (
    <PageArea>
      <PageTitleWithButtons title={t('title')}
        subTitle='Record and track your stock count zones'
        sx={{ mb: '6px' }}>
        {hasPermissionToWriteZones && (hasMoreThanNineZones ? (<Tooltip title={t('table.zones10Tooltip')}>
          <Box>
            <Button
              variant='primary'
              size='lg'
              sx={{ ml: 4 }}
              disabled={hasMoreThanNineZones}
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}>{t('createButton')}
            </Button>
          </Box>
        </Tooltip>) : (
          <Button
            variant='primary'
            size='lg'
            sx={{ ml: 4 }}
            disabled={hasMoreThanNineZones}
            onClick={handleNewZoneBtnClick}
            startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}>{t('createButton')}
          </Button>
        ))}
      </PageTitleWithButtons>
      {isLoading ? <Skeleton variant='rectangular'
        sx={{ borderRadius: '12px' }}
        height={554} /> :
        <Section>
          {data.length === 0 ? <EmptyTable text={'Zones'}
            onClear={() => { }}
            onAdd={handleNewZoneBtnClick}
            addItemText={t('createButton')}
            onAddIsCreating={false}
            headerHeight={55}
            bodyRowHeight={45}
            withPaginationShown={false}
            showClear={false}
            showSecondaryText={false} /> :
            <>
              <TableHeader>
                {t('title')}
                {data.length && <Pill text={`${data.length} Zones`}></Pill>}
              </TableHeader>
              <TableContainer sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                <Table aria-label="stock-count-zones-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t('table.number')}
                      </TableCell>
                      <TableCell>
                        {t('table.name')}
                      </TableCell>
                      {hasPermissionToWriteZones && <TableCell align="right">
                      </TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.length === 0 ? <TableRow><TableCell align='center'
                      colSpan={hasPermissionToWriteZones ? 3 : 2}>{t('table.noZone')}</TableCell></TableRow> :
                      (data.map((zone, index) => (
                        <TableRow key={zone.id}>
                          <TableCell>
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            {zone.name}
                          </TableCell>
                          {hasPermissionToWriteZones && <TableCell align="right">
                            <Tooltip title={t('table.editZoneTooltip')}>
                              <IconButton
                                sx={{ mr: '10px' }}
                                size='small'
                                onClick={(event) => handleEditZoneBtnClick(event, zone.id ?? -1)}>
                                <SvgIcon color='primary'><Edit05 /></SvgIcon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('table.deleteZoneTooltip')}>
                              <IconButton
                                size='small'
                                onClick={(event) => handleDeleteZoneBtnClick(event, zone.id ?? -1)}>
                                <SvgIcon color='error'><Trash01 /></SvgIcon>
                              </IconButton>
                            </Tooltip>
                          </TableCell>}
                        </TableRow>
                      )))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>}
        </Section>}
      <StockCountZonesCreateEditDialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        isSubmitZoneProcessing={isSubmitZoneProcessing}
        isErrorNameTxtF={isErrorNameTxtF}
        errorNameTxtFDescr={errorNameTxtFDescr}
        nameTxtFValue={nameTxtFValue}
        handleNameTextFieldChange={handleNameTextFieldChange}
        handleAddNewZoneConfirm={handleAddNewZoneConfirm}
        handleEditZoneConfirm={handleEditZoneConfirm}
        error={zoneCreationErrorResponse}
        isSubmitBtnDisabled={isSubmitBtnDisabled}
        candidateZoneToEdit={candidateZoneToEdit}
      />
      <ConfirmationDeleteDialog
        open={isDeleteDialogOpen}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleDeleteZoneConfirm}
        nameOfObject={data.find((zone) => zone.id === candidateZoneToDelete)?.name ?? ''} />
      <MessagesSnackbar
        open={showCreationSuccessAlert}
        onClose={() => setShowCreationSuccessAlert(false)}
        message={t('createDialog.successCreation')}
        severity="success" />
      <MessagesSnackbar
        open={showDeletionSuccessAlert}
        onClose={() => setShowDeletionSuccessAlert(false)}
        message={t('deleteDialog.successDeletion')}
        severity="success"
      />
    </PageArea >
  );
};

export default StockCountZonesPage;